import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader2,
  Image,
  FileText,
  Calendar,
  User,
  Phone,
  Mail,
  Banknote,
  Tag,
  MessageSquare,
  Save,
  Heart,
  Receipt,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import type { Donation } from '../types';

export default function AdminDonationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [donation, setDonation] = useState<Donation | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState('');
  const [proofError, setProofError] = useState(false);

  const API_BASE = import.meta.env.VITE_API_URL || '';

  useEffect(() => {
    if (!id) return;
    fetchDonation();
  }, [id]);

  const fetchDonation = async () => {
    setLoading(true);
    const res = await apiFetch<Donation>(`/api/admin/donations/${id}`);
    if (res.success && res.data) {
      setDonation(res.data);
      setAdminRemarks((res.data as any).adminRemarks || '');
    }
    setLoading(false);
  };

  const handleConfirm = async () => {
    if (!donation) return;
    if (!confirm('確認已收到這筆捐款？\n\n系統將更新捐款狀態為「已核實」。')) return;

    setSaving(true);
    const res = await apiFetch(`/api/admin/donations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ payment_status: 'confirmed' }),
    });
    setSaving(false);

    if (res.success) {
      setDonation((prev) => (prev ? { ...prev, paymentStatus: 'confirmed' } : null));
      alert('捐款已核實！');
    } else {
      alert(res.error?.message || '核實失敗');
    }
  };

  const handleReject = async () => {
    if (!donation) return;
    if (!confirm('確定要駁回這筆捐款？\n\n狀態將變為「失敗」。')) return;

    setSaving(true);
    const res = await apiFetch(`/api/admin/donations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ payment_status: 'failed' }),
    });
    setSaving(false);

    if (res.success) {
      setDonation((prev) => (prev ? { ...prev, paymentStatus: 'failed' } : null));
      alert('捐款已駁回。');
    } else {
      alert(res.error?.message || '操作失敗');
    }
  };

  const handleSyncWall = async () => {
    if (!donation) return;
    if (!confirm('將這筆捐款同步到感謝墻？\n\n系統會為這筆捐款創建一條感謝墻留言。')) return;

    setSaving(true);
    const res = await apiFetch(`/api/admin/donations/${id}/sync-wall`, {
      method: 'POST',
    });
    setSaving(false);

    if (res.success) {
      alert('已成功同步到感謝墻！');
    } else {
      alert(res.error?.message || '同步失敗');
    }
  };

  const handleSaveRemarks = async () => {
    if (!donation) return;
    setSaving(true);
    const res = await apiFetch(`/api/admin/donations/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ admin_remarks: adminRemarks }),
    });
    setSaving(false);
    if (res.success) {
      alert('備註已保存');
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const statusConfig: Record<string, { label: string; className: string; icon: React.ElementType }> = {
    pending: { label: '待核實', className: 'bg-amber-100 text-amber-700 border-amber-200', icon: Loader2 },
    confirmed: { label: '已核實', className: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
    failed: { label: '已駁回', className: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
  };

  const methodLabel = (m: string) => {
    const labels: Record<string, string> = {
      fps: 'FPS 轉數快',
      payme: 'PayMe',
      'bank-transfer': '銀行轉賬',
      'credit-card': '信用卡',
      other: '其他',
    };
    return labels[m] || m;
  };

  const typeLabel = (t: string) => {
    const labels: Record<string, string> = {
      'one-time': '一次性捐款',
      'monthly-first': '月捐（首筆）',
      'monthly-recurring': '月捐（續期）',
    };
    return labels[t] || t;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!donation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">找不到捐款記錄</p>
        <button onClick={() => navigate('/admin/donations')} className="btn-secondary">
          <ArrowLeft className="w-4 h-4" />
          返回捐款列表
        </button>
      </div>
    );
  }

  const status = statusConfig[donation.paymentStatus];
  const StatusIcon = status.icon;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/donations')}
          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">捐款詳情</h1>
          <p className="text-sm text-gray-500 font-mono">{donation.donationNum}</p>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`flex items-center gap-3 px-5 py-4 rounded-2xl border ${status.className}`}>
        <StatusIcon className="w-6 h-6" />
        <div>
          <div className="font-bold text-lg">{status.label}</div>
          <div className="text-sm opacity-80">
            {donation.paymentStatus === 'pending' && '等待管理員核實付款憑證'}
            {donation.paymentStatus === 'confirmed' && `核實時間：${donation.confirmedAt ? new Date(donation.confirmedAt).toLocaleString('zh-HK') : '—'}`}
            {donation.paymentStatus === 'failed' && '此筆捐款已被駁回'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Donor Info */}
        <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6 space-y-5">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <User className="w-5 h-5 text-brand-600" />
            捐款人資料
          </h2>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-1.5">
                <User className="w-4 h-4" /> 姓名
              </span>
              <span className="font-medium text-gray-900">
                {donation.isAnonymous ? <span className="text-gray-400">匿名</span> : donation.donorName}
              </span>
            </div>

            {(donation as any).donorPhone && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Phone className="w-4 h-4" /> 電話
                </span>
                <span className="font-medium text-gray-900">{(donation as any).donorPhone}</span>
              </div>
            )}

            {(donation as any).donorEmail && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> 電郵
                </span>
                <span className="font-medium text-gray-900">{(donation as any).donorEmail}</span>
              </div>
            )}

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Banknote className="w-4 h-4" /> 捐款金額
                </span>
                <span className="text-2xl font-extrabold text-brand-600">
                  HK${(donation.amount / 100).toLocaleString()}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-1.5">
                <Tag className="w-4 h-4" /> 類型
              </span>
              <span className="font-medium text-gray-900">{typeLabel(donation.donationType)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-1.5">
                <Banknote className="w-4 h-4" /> 支付方式
              </span>
              <span className="font-medium text-gray-900">{methodLabel(donation.paymentMethod)}</span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500 flex items-center gap-1.5">
                <Calendar className="w-4 h-4" /> 提交時間
              </span>
              <span className="font-medium text-gray-900">
                {new Date(donation.createdAt).toLocaleString('zh-HK')}
              </span>
            </div>

            {/* 扣稅收據 */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Receipt className="w-4 h-4" /> 扣稅收據
                </span>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  (donation as any).receiptRequested
                    ? 'bg-green-50 text-green-600'
                    : 'bg-gray-100 text-gray-500'
                }`}>
                  {(donation as any).receiptRequested ? '需要' : '不需要'}
                </span>
              </div>
              {(donation as any).receiptRequested && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">開據狀態</span>
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                    (donation as any).receiptIssued
                      ? 'bg-blue-50 text-blue-600'
                      : 'bg-amber-50 text-amber-600'
                  }`}>
                    {(donation as any).receiptIssued ? '已開據' : '未開據'}
                  </span>
                </div>
              )}
            </div>

            {donation.message && (
              <div className="bg-gray-50 rounded-xl p-4">
                <span className="text-sm text-gray-500 flex items-center gap-1.5 mb-1">
                  <MessageSquare className="w-4 h-4" /> 留言
                </span>
                <p className="text-gray-700 text-sm leading-relaxed">{donation.message}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Payment Proof & Actions */}
        <div className="space-y-6">
          {/* Payment Proof */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
            <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
              <Image className="w-5 h-5 text-brand-600" />
              付款憑證
            </h2>

            {donation.paymentProofUrl ? (
              <div>
                {proofError ? (
                  <div className="bg-gray-50 rounded-xl p-8 text-center">
                    <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">無法預覽圖片，請點擊下方連結查看</p>
                    <a
                      href={`${API_BASE}/api/admin/donations/${donation.id}/proof`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline text-sm mt-2 inline-block"
                    >
                      打開憑證文件
                    </a>
                  </div>
                ) : (
                  <a
                    href={`${API_BASE}/api/admin/donations/${donation.id}/proof?token=${localStorage.getItem('admin_token') || ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src={`${API_BASE}/api/admin/donations/${donation.id}/proof?token=${localStorage.getItem('admin_token') || ''}`}
                      alt="付款憑證"
                      className="w-full rounded-xl border border-gray-100 hover:opacity-90 transition-opacity"
                      onError={() => setProofError(true)}
                    />
                  </a>
                )}
                <p className="text-xs text-gray-400 mt-2 text-center">
                  點擊圖片可放大查看
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-8 text-center">
                <Image className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">暫未上傳付款憑證</p>
              </div>
            )}
          </div>

          {/* Admin Remarks */}
          <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
            <h2 className="font-bold text-gray-900 flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-brand-600" />
              管理員備註
            </h2>
            <textarea
              value={adminRemarks}
              onChange={(e) => setAdminRemarks(e.target.value)}
              rows={3}
              placeholder="記錄核實過程中的備註..."
              className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm mb-3"
            />
            <button
              onClick={handleSaveRemarks}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-xl transition-colors text-sm disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              保存備註
            </button>
          </div>

          {/* Actions */}
          {donation.paymentStatus === 'pending' && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
              <h2 className="font-bold text-gray-900 mb-4">核實操作</h2>
              <div className="space-y-3">
                <button
                  onClick={handleConfirm}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition-all disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  確認收到捐款
                </button>
                <button
                  onClick={handleReject}
                  disabled={saving}
                  className="w-full flex items-center justify-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold py-3 px-6 rounded-xl transition-all border border-red-200 disabled:opacity-50"
                >
                  <XCircle className="w-5 h-5" />
                  駁回捐款
                </button>
              </div>
            </div>
          )}

          {donation.paymentStatus === 'confirmed' && (
            <div className="bg-white rounded-2xl shadow-sm border border-brand-100 p-6">
              <h2 className="font-bold text-gray-900 mb-4">感謝墻</h2>
              <button
                onClick={handleSyncWall}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-orange-50 hover:bg-orange-100 text-orange-700 font-bold py-3 px-6 rounded-xl transition-all border border-orange-200 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Heart className="w-5 h-5" />}
                同步到感謝墻
              </button>
              <p className="text-xs text-gray-400 mt-2 text-center">
                若核實時未自動創建，可手動補上
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
